import { useQuery } from 'react-query'

// Use the backend URL from environment or fallback to localhost
const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000/api'

// Get API key from localStorage
const getApiKey = () => {
    return localStorage.getItem('graniot_public_api_key')
}

// Fetch job GeoJSON with deforestation risk
export const fetchJobGeoJSON = async (jobUuid) => {
    const apiKey = getApiKey()
    if (!apiKey) {
        throw new Error('Missing public API key (auth_pk).')
    }

    const url = `${API_URL}/bulk_deforestation/job-geojson/${jobUuid}/`

    const response = await fetch(url, {
        headers: {
            'x-api-key': apiKey,
            'Content-Type': 'application/json',
        },
    })

    if (!response.ok) {
        const status = response.status
        // Keep messages simple for public: never surface raw 50x
        if (status >= 500) {
            throw new Error('We’re having trouble loading the map data. Please try again later.')
        }
        if (status === 401 || status === 403) {
            throw new Error('Your public API key is missing or invalid.')
        }
        const errorData = await response.json().catch(() => null)
        throw new Error(errorData?.error || 'We couldn’t load the map data. Please check the link.')
    }

    const data = await response.json()

    // Ensure all features have a string name property
    if (data && data.features) {
        data.features = data.features.map(feature => ({
            ...feature,
            properties: {
                ...feature.properties,
                // Ensure name is always a string
                name: String(feature.properties?.name || feature.id || 'Unnamed')
            }
        }))
    }

    return data
}

// React Query hook for job GeoJSON
export const useJobGeoJSON = (jobUuid) => {
    const apiKey = getApiKey()
    const enabled = !!jobUuid && !!apiKey

    return useQuery(
        ['jobGeoJSON', jobUuid],
        () => fetchJobGeoJSON(jobUuid),
        {
            enabled: enabled,
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 10 * 60 * 1000, // 10 minutes
            retry: 2,
            retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
            onError: (error) => {
                console.error('Query error:', error)
            }
        }
    )
}
